import type { RefObject } from 'react'
import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type {
  OrderUpdateEventMessage,
  TradingEventMessage,
} from '@/types/api/events/trading-event'

import { cloneDeep } from 'lodash'

import { createLogger } from '@/lib/logger'
import {
  type OrderCancelEvent,
  type OrderRejectEvent,
  OrderUpdateEventType,
} from '@/types/api/events/order-update'

import { updateLatestMsgNumber, upsertOrder } from './helpers'

const LOGGER = createLogger('utils/trade-blotter/trading-events/order-update')

function constructUpdatedOrderView(
  orderUpdateEventMessage: OrderUpdateEventMessage,
  existingOrderView: AggregatedOrderView | null,
): AggregatedOrderView {
  const { event: orderUpdateEvent, order: orderFromEvent } =
    orderUpdateEventMessage
  const existingTradingEvents = existingOrderView?.trading_events || []
  // Want to prevent duplicate trading events from appearing in the blotter
  // in the event we receive a duplicate message because we reconnected to
  // the websocket at a high water mark we have already processed.
  const filteredTradingEvents = existingTradingEvents.filter(
    (event: TradingEventMessage) =>
      event.msg_number !== orderUpdateEventMessage.msg_number,
  )
  const createdAt =
    orderUpdateEvent.event_type === OrderUpdateEventType.Request
      ? orderUpdateEvent.time
      : existingOrderView?.created_at || null
  const rejectReason =
    orderUpdateEvent.event_type === OrderUpdateEventType.Reject
      ? (orderUpdateEvent as OrderRejectEvent).reject_reason
      : existingOrderView?.reject_reason || null
  const cancelReason =
    orderUpdateEvent.event_type === OrderUpdateEventType.Cancel
      ? (orderUpdateEvent as OrderCancelEvent).cancel_reason
      : existingOrderView?.cancel_reason || null
  return updateLatestMsgNumber(
    {
      ...orderFromEvent,
      created_at: createdAt,
      allocation_status: existingOrderView?.allocation_status || null,
      allocation_info: existingOrderView?.allocation_info || null,
      account_requests: existingOrderView?.account_requests || null,
      // TODO: We will need to refactor this when we can do multi-custodial trades
      custodian_id: existingOrderView?.custodian_id || null,
      last_allocation_update_event:
        existingOrderView?.last_allocation_update_event || null,
      last_order_update_event: orderUpdateEvent,
      last_system_update_event:
        existingOrderView?.last_system_update_event || null,
      // Cloning since we are concatenating to the array and AG Grid needs a new reference to detect changes
      trading_events: cloneDeep(
        filteredTradingEvents.concat(orderUpdateEventMessage),
      ),
      rfq_views: existingOrderView?.rfq_views || null,
      reject_reason: rejectReason,
      cancel_reason: cancelReason,
    },
    orderUpdateEventMessage.msg_number,
  )
}

/**
 * Processes order updates received via HTTP and updates the ordersMap.
 * Only called as part of the transformResponse callback in the TradingSlice.
 * @param aggregatedOrderViewsMap Map of existing AggregatedOrderView objects
 * @param tradingEvent Incoming trading event
 */
export const processOrderUpdate = (
  aggregatedOrderViewsMap: Map<string, AggregatedOrderView>,
  tradingEvent: TradingEventMessage,
): void => {
  const orderUpdateEventMessage = tradingEvent as OrderUpdateEventMessage
  const orderId = orderUpdateEventMessage.order.id
  const existingOrderView = aggregatedOrderViewsMap.get(orderId) || null
  const updatedOrderView = constructUpdatedOrderView(
    orderUpdateEventMessage,
    existingOrderView,
  )
  aggregatedOrderViewsMap.set(orderId, updatedOrderView)
}

/**
 * Handles order updates received via WebSocket and updates the data cached in Redux.
 * @param tradingEvent Incoming trading event
 * @param updateCachedData Function to update the cached data in Redux
 */
export const handleOrderUpdate = (
  tradingEvent: TradingEventMessage,
  aggregatedOrderViewsRef: RefObject<Map<string, AggregatedOrderView>>,
  onUpdateApplied: ({
    toAdd,
    toUpdate,
    toRemove,
  }: {
    toAdd?: AggregatedOrderView[]
    toUpdate?: AggregatedOrderView[]
    toRemove?: AggregatedOrderView[]
  }) => void,
) => {
  const orderUpdateEventMessage = tradingEvent as OrderUpdateEventMessage

  LOGGER.info('Order WebSocket event received', {
    browserTime: new Date().toISOString(),
    eventId: orderUpdateEventMessage.event_id,
    eventTime: orderUpdateEventMessage.event.time,
    eventType: orderUpdateEventMessage.event.event_type,
    msgNumber: orderUpdateEventMessage.msg_number,
    orderId: orderUpdateEventMessage.order.id,
    rfqIds: orderUpdateEventMessage.order.rfqs?.map((rfq) => rfq.id),
  })

  const orderId = orderUpdateEventMessage.order.id

  const map = aggregatedOrderViewsRef.current
  const existingOrderView = map.get(orderId) || null
  const updatedOrderView = constructUpdatedOrderView(
    orderUpdateEventMessage,
    existingOrderView,
  )
  upsertOrder(map, orderId, updatedOrderView, onUpdateApplied)
}
